import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '../../../shared/i18n';

/**
 * Shared paginated transactions view, used by both the guardian and kid
 * dedicated transactions pages.
 *
 * Props:
 *   currencyId    -- numeric id of the family currency
 *   api           -- { transactions(id, params) } API client (guardian or kid)
 *   backHref      -- optional href for the "Back" link
 */
const pad = (n) => String(n).padStart(2, '0');
const formatTransactionTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const TransactionsView = ({ currencyId, api, backHref }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .transactions(currencyId, { page })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(t('family_currencies.errors.generic'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currencyId, page, api, t]);

  const transactions = data?.transactions || [];
  const pagination = data?.pagination || { page: 1, total_pages: 1, has_next: false, has_prev: false };
  const currency = data?.family_currency;

  return (
    <div className="bg-light">
      <div className="container py-4" data-testid={`transactions-page-${currencyId}`}>
        {backHref && (
          <Link to={backHref} className="btn btn-link p-0 mb-3" data-testid="transactions-back-link">
            ← {t('family_currencies.transactions.back')}
          </Link>
        )}
        <h2 className="h3 fw-bold mb-2">
          {t('family_currencies.transactions.page_title', { name: currency?.name || '…' })}
        </h2>
        <p className="text-muted">
          {t('family_currencies.balance', { balance: currency?.balance ?? 0 })}
        </p>

        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-muted" data-testid={`transactions-empty-${currencyId}`}>
            {t('family_currencies.transactions.empty')}
          </p>
        ) : (
          <>
            <table className="table table-sm" data-testid={`transactions-table-${currencyId}`}>
              <thead>
                <tr>
                  <th>{t('family_currencies.transactions.kind')}</th>
                  <th>{t('family_currencies.transactions.amount')}</th>
                  <th>{t('family_currencies.transactions.snapshotted_balance')}</th>
                  <th>{t('family_currencies.transactions.counterparty')}</th>
                  <th>{t('family_currencies.transactions.memo')}</th>
                  <th>{t('family_currencies.transactions.when')}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id} data-testid={`tx-row-${tx.id}`}>
                    <td data-testid={`tx-kind-${tx.id}`}>
                      {tx.kind === 'credit'
                        ? t('family_currencies.transactions.credit')
                        : t('family_currencies.transactions.debit')}
                    </td>
                    <td>
                      {tx.kind === 'credit' ? '+' : '−'}
                      {tx.amount}
                    </td>
                    <td data-testid={`tx-snapshotted-balance-${tx.id}`}>
                      {tx.snapshotted_balance ?? '-'}
                    </td>
                    <td>{tx.counterparty?.label || '-'}</td>
                    <td>{tx.memo || ''}</td>
                    <td>
                      <span
                        className="text-muted small font-monospace"
                        data-testid={`tx-when-${tx.id}`}
                      >
                        {formatTransactionTime(tx.created_at)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <nav data-testid={`transactions-pagination-${currencyId}`}>
              <ul className="pagination">
                <li className={`page-item ${pagination.has_prev ? '' : 'disabled'}`}>
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => pagination.has_prev && setPage((p) => Math.max(1, p - 1))}
                    disabled={!pagination.has_prev}
                    data-testid="transactions-prev-page"
                  >
                    ‹ {t('family_currencies.transactions.prev')}
                  </button>
                </li>
                <li className="page-item disabled">
                  <span className="page-link">
                    {t('family_currencies.transactions.page_of', {
                      page: pagination.page,
                      total: pagination.total_pages
                    })}
                  </span>
                </li>
                <li className={`page-item ${pagination.has_next ? '' : 'disabled'}`}>
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => pagination.has_next && setPage((p) => p + 1)}
                    disabled={!pagination.has_next}
                    data-testid="transactions-next-page"
                  >
                    {t('family_currencies.transactions.next')} ›
                  </button>
                </li>
              </ul>
            </nav>
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionsView;
